import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { rupiah } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import PayslipCard from "@/components/PayslipCard";
import SlipShare from "@/components/SlipShare";
import SlipBulkShare from "@/components/SlipBulkShare";
import ExportPreview from "@/components/ExportPreview";
import { F } from "@/pages/Employees";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer, Eye, FileDown, MessageCircle, Share2 } from "lucide-react";

const SUM = ["gaji_pokok", "uang_makan", "uang_luar_kota", "uang_lembur", "total_tunjangan",
  "gaji_kotor", "kasbon", "potongan_lain", "total_potongan", "gaji_bersih", "hari_masuk", "jam_lembur"];
function mergeByEmployee(details) {
  const groups = {};
  details.forEach((d) => { (groups[d.employee_id] = groups[d.employee_id] || []).push(d); });
  return Object.values(groups).map((ds) => {
    const base = { ...ds[0] };
    if (ds.length > 1) {
      SUM.forEach((f) => { base[f] = ds.reduce((s, x) => s + Number(x[f] || 0), 0); });
      base.project_nama = "Beberapa Proyek";
      base.projects = ds.map((x) => ({ nama: x.project_nama || "-", hari: x.hari_masuk, lembur: x.jam_lembur, bersih: x.gaji_bersih }));
    }
    return base;
  }).sort((a, b) => (a.employee_nama || "").localeCompare(b.employee_nama || ""));
}

export default function Payslips() {
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState("");
  const [period, setPeriod] = useState(null);
  const [details, setDetails] = useState([]);
  const [company, setCompany] = useState({});
  const [preview, setPreview] = useState(null);
  const [printAll, setPrintAll] = useState(false);
  const [previewAll, setPreviewAll] = useState(false);
  const [share, setShare] = useState(null);
  const [empMap, setEmpMap] = useState({});
  const [picked, setPicked] = useState({});
  const [bulkOpen, setBulkOpen] = useState(false);
  const pickedList = details.filter((d) => picked[d.id]);
  const allPicked = details.length > 0 && details.every((d) => picked[d.id]);

  useEffect(() => {
    api.get("/payroll/periods").then((r) => setPeriods(r.data));
    api.get("/settings").then((r) => setCompany(r.data));
    api.get("/employees").then((r) => {
      const m = {};
      (r.data || []).forEach((e) => { m[e.id] = e.no_wa || e.no_hp || ""; });
      setEmpMap(m);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!periodId) return;
    api.get(`/payroll/periods/${periodId}`).then((r) => setPeriod(r.data));
    api.get(`/payroll/periods/${periodId}/details`).then((r) => { setDetails(mergeByEmployee(r.data)); setPicked({}); });
  }, [periodId]);

  const doPrint = (all) => {
    setPrintAll(all);
    setTimeout(() => { window.print(); setPrintAll(false); }, 200);
  };

  const dlBlob = (data, name) => {
    const url = URL.createObjectURL(data);
    const a = document.createElement("a"); a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  };
  const downloadOnePdf = async (id, nama) => {
    const r = await api.get(`/payroll/details/${id}/slip.pdf`, { responseType: "blob" });
    dlBlob(r.data, `slip_${nama}.pdf`);
  };

  return (
    <div className="space-y-6">
      <div className="no-print space-y-6">
        <PageHeader title="Slip Gaji" subtitle="Preview, cetak, dan simpan PDF slip gaji">
          {details.length > 0 && <>
            <Button variant="outline" onClick={() => setPreviewAll(true)} data-testid="slip-download-all"><FileDown className="h-4 w-4 mr-1" /> Pratinjau & Unduh Semua</Button>
            <Button className="bg-blue-800 hover:bg-blue-900" onClick={() => doPrint(true)} data-testid="slip-print-all"><Printer className="h-4 w-4 mr-1" /> Cetak Semua ({details.length})</Button>
          </>}
        </PageHeader>
        <Card className="p-4 max-w-md">
          <F label="Pilih Periode">
            <Select value={periodId} onValueChange={setPeriodId}>
              <SelectTrigger data-testid="slip-period-select"><SelectValue placeholder="Pilih periode" /></SelectTrigger>
              <SelectContent>{periods.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama} — {p.status}</SelectItem>)}</SelectContent>
            </Select>
          </F>
        </Card>

        {details.length > 0 && (
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" className="h-4 w-4 accent-emerald-600" checked={allPicked}
              onChange={(e) => setPicked(e.target.checked ? Object.fromEntries(details.map((d) => [d.id, true])) : {})} data-testid="slip-pick-all" /> Pilih semua</label>
            <Button className="bg-[#25D366] hover:bg-[#1ebe5b] text-white" disabled={!pickedList.length} onClick={() => setBulkOpen(true)} data-testid="slip-bulk-share-btn">
              <Share2 className="h-4 w-4 mr-1" /> Bagikan Terpilih ({pickedList.length})</Button>
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {details.map((d, i) => (
            <Card key={d.id} className={`p-4 flex items-center justify-between ${picked[d.id] ? "ring-2 ring-emerald-400" : ""}`} data-testid={`slip-item-${i}`}>
              <div className="flex items-start gap-3">
                <input type="checkbox" className="mt-1 h-4 w-4 accent-emerald-600" checked={!!picked[d.id]} onChange={(e) => setPicked({ ...picked, [d.id]: e.target.checked })} data-testid={`slip-pick-${i}`} />
              <div>
                <div className="font-semibold text-slate-800">{d.employee_nama}</div>
                <div className="text-xs text-slate-500">{d.project_nama}</div>
                <div className="font-mono font-bold text-slate-900 mt-1">{rupiah(d.gaji_bersih)}</div>
              </div>
              </div>
              <div className="flex flex-col gap-1">
                <Button size="sm" variant="outline" data-testid={`slip-preview-${i}`} onClick={() => setPreview(d)}><Eye className="h-4 w-4 mr-1" /> Lihat</Button>
                <Button size="sm" className="bg-[#25D366] hover:bg-[#1ebe5b] text-white" data-testid={`slip-wa-${i}`} onClick={() => setShare(d)}><MessageCircle className="h-4 w-4 mr-1" /> Kirim WA</Button>
                <Button size="sm" variant="ghost" data-testid={`slip-pdf-${i}`} onClick={() => downloadOnePdf(d.id, d.employee_nama)}><FileDown className="h-4 w-4 mr-1" /> PDF</Button>
              </div>
            </Card>
          ))}
        </div>
        {periodId && details.length === 0 && <div className="text-center text-slate-400 py-10">Belum ada data. Hitung payroll terlebih dahulu.</div>}
      </div>

      {/* Print area */}
      <div className={printAll ? "print-area space-y-4" : "hidden print:block"}>
        {printAll && details.map((d) => (
          <PayslipCard key={d.id} detail={d} period={period} company={company} />
        ))}
      </div>

      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
          <div className="print-area p-4">
            {preview && <PayslipCard detail={preview} period={period} company={company} />}
          </div>
          <div className="no-print flex justify-end gap-2 p-4 pt-0">
            <Button variant="outline" onClick={() => setPreview(null)}>Tutup</Button>
            <Button className="bg-[#25D366] hover:bg-[#1ebe5b] text-white" onClick={() => { const d = preview; setPreview(null); setShare(d); }} data-testid="slip-wa-single"><MessageCircle className="h-4 w-4 mr-1" /> Kirim WA</Button>
            <Button variant="outline" onClick={() => downloadOnePdf(preview.id, preview.employee_nama)} data-testid="slip-dl-single"><FileDown className="h-4 w-4 mr-1" /> Unduh PDF</Button>
            <Button className="bg-blue-800 hover:bg-blue-900" onClick={() => window.print()} data-testid="slip-print-single"><Printer className="h-4 w-4 mr-1" /> Cetak / PDF</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ExportPreview open={previewAll} onClose={() => setPreviewAll(false)} title="Pratinjau Slip Gaji (Semua)"
        pdfUrl={periodId ? `/payroll/periods/${periodId}/slips.pdf` : null}
        onPrint={() => doPrint(true)}
        items={[{ label: "Unduh PDF Semua", url: `/payroll/periods/${periodId}/slips.pdf`, filename: "slip_gaji.pdf" }]} />

      <SlipShare open={!!share} onClose={() => setShare(null)} detail={share} period={period} companyName={company?.nama_perusahaan || "CPS PAYROLL"} nomorWa={share ? (empMap[share.employee_id] || "") : ""} />
      <SlipBulkShare open={bulkOpen} onClose={() => setBulkOpen(false)} details={pickedList} period={period} companyName={company?.nama_perusahaan || "COATING SOLUTION"} empMap={empMap} />
    </div>
  );
}
